import React from 'react';
import { ApiPlanFrequency, Price } from '../types';
import { CountryCurrency, findPriceForCountryCurrency } from '../../../../helpers';
import '../Pricing.scss';

export type PricingPlanProductDocument = {
    __typename: string;
    data: {
        name: string;
        unlimited_text: string;
        discount_text: string;
        api_product: {
            code: string;
        };
    };
};

export type PricingPlanProductProps = {
    product: PricingPlanProductDocument;
    apiPlanFrequency: ApiPlanFrequency;
    countryCurrency: CountryCurrency;
};

/**
 * Returns the price of a product for the specified plan
 * @param {ApiPlanFrequency} apiPlanFrequency - The API plan frequency to search
 * @param {string} code - The code of the desired product
 * @param {CountryCurrency} countryCurrency - The price's currency code
 */
export const getProductPrice = (
    apiPlanFrequency: ApiPlanFrequency,
    code: string,
    countryCurrency: CountryCurrency,
): Price => {
    const fallback = {} as Price;

    if (apiPlanFrequency && apiPlanFrequency.planProducts) {
        const product = apiPlanFrequency.planProducts.find(p => p.code === code);

        if (product && product.quantityRanges) {
            // Returns the last range of tiered pricing. We assume there is only 1 or 2 prices:
            // When there is 2, the first quantityRange is free, the second has a price.
            // If we have more than 2, we'll need to update our pricing page UX to reflect that.
            const range = product.quantityRanges.slice(-1).pop();

            if (range && range.prices) {
                const price = findPriceForCountryCurrency(range.prices, countryCurrency);
                return price || fallback;
            }
        }
    }

    return fallback;
};

export class PricingPlanProduct extends React.Component<PricingPlanProductProps> {
    render() {
        const { apiPlanFrequency, countryCurrency } = this.props;
        const product = this.props.product.data;
        const price = getProductPrice(apiPlanFrequency, product.api_product.code, countryCurrency);

        return (
            <li className="PricingPlanProduct">
                <span className="PricingPlanProduct-name">{product.name}</span>
                &nbsp;
                {price && (
                    <span className="PricingPlanProduct-details">
                        {price.amount
                            ? `${price.currencySymbol}${price.amount}`
                            : product.unlimited_text}
                    </span>
                )}
            </li>
        );
    }
}
